//@ts-nocheck
import { getFieldInstanceById, setFieldInstanceToTouched } from "db/queries/fieldInstanceQueries";
import useCustomForm from "hooks/useCustomForm";
import { useTheme } from "hooks/useSingleton";
import React, { useEffect, useState } from "react";
import updateImageDoc from "utils/_updateImageDoc";
import { FieldInstance, Image, User } from "db/definitions";
import { NumberField, Thumbnail } from "@factorearth/component-library";
import { getImageBinaryById } from "db/queries/imageBinaryQueries";
import { getCorrectS3Url } from "utils/s3/getCorrectS3Url";
import { recordSentry } from "utils/recordSentry";
import _getFieldInstance from "utils/_getFieldInstance";

const ImageSettings = ({ values, handleChangeInput, colorPalette }: any) => {
	const smallNumberStyles = {
		input: { width: "87px", height: "37px" },
		div: { width: "14px", height: "21px", padding: "8px 12px" },
	};

	return (
		<div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
			<div style={{ display: "flex" }}>
				<NumberField
					name="rotation"
					colorPalette={colorPalette}
					handleChange={handleChangeInput}
					handleClickAndSubmit={handleChangeInput}
					value={values.rotation}
					styles={smallNumberStyles}
					placeholder="Rotation"
					rotation={90}
				/>
			</div>
			<div style={{ display: "flex" }}>
				<NumberField
					name="aspectX"
					colorPalette={colorPalette}
					handleChange={handleChangeInput}
					handleClickAndSubmit={handleChangeInput}
					value={values.aspectX}
					styles={smallNumberStyles}
					placeholder="AspectX"
				/>
			</div>
			<div style={{ display: "flex" }}>
				<NumberField
					name="aspectY"
					colorPalette={colorPalette}
					handleChange={handleChangeInput}
					handleClickAndSubmit={handleChangeInput}
					value={values.aspectY}
					styles={smallNumberStyles}
					placeholder="AspectY"
				/>
			</div>
		</div>
	);
};
interface Props {
	fieldInstance: FieldInstance;
	handleChangeCb: (data: { target: { name?: string; value: string | Image } }) => Promise<void>;
	online: boolean;
	setupChanged: React.Dispatch<React.SetStateAction<boolean>>;
	userDoc: User;
	fromTable: boolean;
	rowData?: FieldInstance[];
}

const ImageField = (props: Props) => {
	const { fieldInstance, handleChangeCb, online, setupChanged, userDoc, fromTable, rowData } = props;
	const [editOpen, setEditOpen] = useState(false);
	const [signedUrl, setSignedUrl] = useState(null);
	const colorPalette = useTheme();
	const [showImage, setShowImage] = useState<boolean>(true);

	const { handleChange, handleSubmit, handleUpdate, handleImageChangeAndUpload, changed, loading, values } = useCustomForm(
		fieldInstance.image ?? { key: "", rotation: "", dpi: "", aspectX: "", aspectY: "" },
		handleSubmitClick,
	);

	useEffect(() => {
		if (fieldInstance?.image) {
			if (fieldInstance.editing !== userDoc.username) {
				if (!fieldInstance.image.group) fieldInstance.image.group = userDoc.group;
				handleUpdate(fieldInstance.image);
			}
		}
	}, [fieldInstance]);

	useEffect(() => {
		if (!values) {
			recordSentry({ error: new Error("ImageField has null image"), message: `fieldInstance id: ${fieldInstance.id}` });
			return;
		}
		if (values.key && typeof values.key === "string" && values.key !== "") {
			handleGetImage();
		} else if (values.key && typeof values.key === "object") {
			setSignedUrl(values.key);
		} else if (values.key === "") {
			setSignedUrl(null);
		}
	}, [values?.key]);

	/**
	 * edit image toggle
	 */
	function handleEditToggle() {
		setEditOpen(!editOpen);
	}

	/**
	 * Remove image handler (x)
	 */
	function handleRemove() {
		// remove the image
		const blankKeyObj = {
			target: {
				name: "key",
				value: "",
			},
		};
		handleImageChangeAndUpload(blankKeyObj);
		setSignedUrl(null);
	}

	function handleChangeInput(e: React.ChangeEvent<HTMLInputElement>) {
		if (!changed) {
			const fakeChange = {
				target: {
					value: fieldInstance.value,
				},
			};
			handleChangeCb(fakeChange);
		}

		handleImageChangeAndUpload(e);
	}

	async function handleSubmitClick(newValues: T) {
		// These are the steps happening in this method:
		// 1. first upload the image
		// 2. filename parsed then
		// 3. this will need to be sent to the toolbox
		// 4. then upload the result of that
		// 5. remove the previous image
		// 6. fieldInstance should still be the initial...
		// 7. setup image in the fieldInstance and handleChange for FieldAction to preserve the state
		if (fieldInstance.image && fieldInstance.tableField) {
			// THIS IS CURRENLTY HARD CODED TO USE THE IMAGE NUMBER FIELD via rollLinkId
			const imageNumberField = await getImageNumberFieldData(rowData);
			let fileNameOverride = null;
			if (imageNumberField && imageNumberField !== "" && typeof imageNumberField === "string") {
				// const file = newValues.key instanceof FileList ? newValues.key[0] : newValues.key instanceof File ? newValues.key : null;
				fileNameOverride = imageNumberField;
			}
			const newValue = { ...newValues, key: { files: newValues?.key } };
			const [updated] = await Promise.all([
				updateImageDoc(fieldInstance.image, newValue, userDoc.group, online ? "UP" : "DOWN", online, false, false, fileNameOverride),
				// setFieldInstanceToTouched(fieldInstance.tableField),
			]);

			const changeObj = {
				target: {
					name: "image",
					value: updated,
				},
			};
			await handleChangeCb(changeObj);
			setupChanged(false);
		}
	}

	async function getImageNumberFieldData(rowData: FieldInstance[]) {
		// THIS IS CURRENLTY HARD CODED TO USE THE IMAGE NUMBER FIELD via rollLinkId
		if (rowData) {
			const imageNumberField = rowData.find((field) => {
				return field?.fieldTemplate?.rollLinkId === "IMAGE_NUM";
			});
			if (imageNumberField) {
				// need to fetch the up to date value for this field
				const updatedImageNumberField = online ? await _getFieldInstance(imageNumberField.id) : await getFieldInstanceById(imageNumberField.id);
				return updatedImageNumberField.value ?? null;
			}
		}
		return null;
	}

	async function handleGetImage() {
		if (online) {
			try {
				const actualUrl = await getCorrectS3Url(values.key);
				setSignedUrl(actualUrl);
			} catch (err) {
				recordSentry({ error: err, message: `Error getting image (does it exist?): ${values.key}` });
			}
		} else {
			const imageBinaryDoc = await getImageBinaryById(values.key);
			if (imageBinaryDoc) {
				const binary = imageBinaryDoc.binary;
				const source = URL.createObjectURL(binary);
				setSignedUrl(source);
			} else {
				const err = new Error(`Error uploading image: ${values.key} Image was not saved offline.`);
				recordSentry({ error: err, message: `Error uploading image: ${values.key} Image was not saved offline.` });
			}
		}
	}

	return (
		<div data-fieldtemplateid={fieldInstance.fieldTemplateId}>
			<Thumbnail
				data-fieldTemplateId={fieldInstance.fieldTemplateId}
				changed={changed}
				handleSubmit={handleSubmit as any}
				loading={loading}
				fieldInstance={fieldInstance}
				s3Key={values.key}
				handleRemove={handleRemove}
				handleChange={handleChangeInput}
				colorPalette={colorPalette}
				showImage={showImage}
				setShowImage={setShowImage}
				signedUrl={signedUrl}
				settingsChildren={<ImageSettings values={values} handleChangeInput={handleChangeInput} colorPalette={colorPalette} />}
				fromTable={fromTable}
			/>
		</div>
	);
};

export default ImageField;
